import { Injectable } from '@angular/core';
import {
  EstablishmentCustomerModel,
  EstablishmentGenericPrestationModel,
  EstablishmentGenericProductModel,
  EstablishmentModel,
} from 'src/app/core/models/establishment.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AddressModel } from '../../../../core/models/address.model';
import { BehaviorSubject, lastValueFrom, Observable, of } from 'rxjs';
import { ObjectApiInterface } from '../../../../core/base/interfaces/object-api.interface';
import { UserProfileService } from '../user-profile/user-profile.service';
import { UserProfileModel } from '../../../../core/models/user-profile.model';
import { ActivityModel } from '../../../../core/models/activity.model';
import { AuthService } from '../../../../core/services/auth.service';
import { AddressService } from '../../../../core/services/address.service';
import { DEFAULT_PAGE_SIZE } from '../../../../core/config/base-table.config';
import { EstablishmentTypeEnum } from '../../../../core/enums/establishment-type.enum';
import { AnnualChargeModel } from '../../../../core/models/annual-charge.model';
import { SupplierContactModel } from '../../../../core/models/supplier-contact.model';
import { ContractCompatibilityAnalysis } from '../../../../core/models/contract.model';
import { BaseService } from 'src/app/core/base/base.service';
import { map, switchMap, tap } from 'rxjs/operators';
import { ActivityForDistributionResponse } from '../../../../core/interfaces/activity-distribution.interface';
import { AbstractControl } from '@angular/forms';
import { AccountLineModel, findOrAppendAccount } from '../../../../core/models/account-chart.model';

@Injectable({
  providedIn: 'root',
})
export class EstablishmentService extends BaseService<EstablishmentModel> {
  defaultSort = 'createdAt';

  protected _contacts = new BehaviorSubject<(UserProfileModel | SupplierContactModel)[]>(
    [] as (UserProfileModel | SupplierContactModel)[],
  );
  readonly contacts$ = this._contacts.asObservable();

  constructor(
    public http: HttpClient,
    private userProfileService: UserProfileService,
    public authService: AuthService,
    private addressService: AddressService,
  ) {
    super(http, 'establishment', authService);
  }

  // async getContact(search?: string) {
  //     this.entity$.pipe(
  //         switchMap((establishment: any) => {
  //             if (establishment?._id) {
  //                 console.log('icxic', establishment._id);
  //                 return this.userProfileService.findAll(null, null, null, 'desc', search, { establishment: establishment._id });
  //             }
  //             return new Observable<any>();
  //         })
  //     ).subscribe((contact) => {
  //         if (contact?.data) {
  //             this._contacts.next(contact.data);
  //         }
  //     });
  // }

  checkDependencies(establishment: EstablishmentModel) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/checkDependencies/${establishment._id}`, {});
  }

  checkActivityDependencies(establishmentId: string, activityId: string) {
    return this.http.post<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${establishmentId}/checkActivityDependencies/${activityId}`,
      {},
    );
  }

  // Wrapper pour utiliser avec DependenciesService
  checkActivityDependenciesWrapper(activity: ActivityModel) {
    // On doit extraire l'establishmentId du contexte
    // Pour l'instant on va utiliser l'entity en cours
    return this.entity$.pipe(
      switchMap((establishment: EstablishmentModel) => {
        if (establishment?._id && activity?._id) {
          return this.checkActivityDependencies(establishment._id, activity._id);
        }
        throw new Error('Establishment or Activity ID missing');
      }),
    );
  }

  deletingValidators() {
    return this.http.delete<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/deleteEmptyValidators`);
  }

  // private filterSupplierContacts(contacts: SupplierContactModel[], search: string): SupplierContactModel[] {
  //     if (!search) { return contacts; }
  //     const term = search.toLowerCase();
  //     return contacts.filter(c => {
  //         const email = c.email?.toLowerCase().includes(term);
  //         const fullName = `${c.firstName} ${c.lastName}`.toLowerCase().includes(term);
  //         const fullNameReverse = `${c.lastName} ${c.firstName}`.toLowerCase().includes(term);
  //         const isSupplierContact = true;
  //         return email || fullName || fullNameReverse || isSupplierContact;
  //     });
  // }

  // getContact(establishmentId?: string, search = ''): Observable<any> {
  //     return this.userProfileService
  //         .findAll(null, null, 'firstName', 'asc', search, { establishment: establishmentId })
  //         .pipe(
  //             map(x => {
  //                 const internalContacts = x.data.map(c => ({ ...c, isEstablishmentContact: false }));
  //                 let establishmentContacts = this._entity.value?.contacts ?? [];
  //                 establishmentContacts = this.filterSupplierContacts(establishmentContacts, search)
  //                     .map(c => ({ ...c, isEstablishmentContact: true }));
  //                 this._contacts.next([...internalContacts, ...establishmentContacts]);
  //                 return x;
  //             })
  //         );
  // }

  getContact(establishmentId?: string, search = ''): Observable<any> {
    const establishment = this._entity.getValue();

    if (establishment?.establishmentType === EstablishmentTypeEnum.SUPPORT_FUNCTION) {
      let contacts = establishment?.contacts ?? [];

      if (search) {
        const term = search.toLowerCase();
        contacts = contacts.filter(c => {
          const email = c.email?.toLowerCase().includes(term);
          const fullName = `${c.firstName} ${c.lastName}`.toLowerCase().includes(term);
          const fullNameReverse = `${c.lastName} ${c.firstName}`.toLowerCase().includes(term);
          return email || fullName || fullNameReverse;
        });
      }

      contacts = contacts.map(c => ({ ...c, isEstablishmentContact: true }));
      this._contacts.next(contacts);
      return of({ data: contacts });
    }

    return this.userProfileService
      .findAll(null, null, 'firstName', 'asc', search, { establishment: establishmentId })
      .pipe(
        map(x => {
          const internalContacts = x.data.map(c => ({
            ...c,
            isEstablishmentContact: false,
            contactTypes: c['contactType'] ?? [],
          }));
          this._contacts.next(internalContacts);
          return { ...x, data: internalContacts };
        }),
      );
  }

  /**
   * Comptes du plan comptable de l'établissement (PRED-3081), pour les listes déroulantes commande/facture.
   * `genericElementId` restreint au compte cible du produit/prestation générique, `accountNumberPrefix` à un préfixe.
   * Si `control` est fourni, sa valeur courante (compte déjà affecté) est remplacée par l'élément
   * correspondant de la liste — ou ajoutée à la liste s'il n'y figure pas — afin qu'elle s'affiche.
   */
  findAccountingAccounts(
    establishmentId: string,
    search?: string,
    filters: { genericElementId?: string; accountNumberPrefix?: string } = {},
    control?: AbstractControl,
  ): Observable<AccountLineModel[]> {
    if (!establishmentId) {
      return of([]);
    }
    return this.http
      .get<ObjectApiInterface<AccountLineModel[]>>(`${this.baseUrlApi}/${establishmentId}/accounting-accounts/list`, {
        params: new HttpParams()
          .set('perPage', '100')
          .set('search', typeof search === 'string' ? search : '')
          .set('filters', JSON.stringify(filters)),
      })
      .pipe(
        map(res => res.data),
        tap(accounts => control && this.syncAccountControl(control, accounts)),
      );
  }

  private syncAccountControl(control: AbstractControl, accounts: AccountLineModel[]): void {
    const current = control.value;
    // Pendant la frappe la valeur est une chaîne : rien à présélectionner
    if (!current?.accountNumber) {
      return;
    }
    // emitEvent false : présélection, pas une saisie utilisateur
    control.setValue(findOrAppendAccount(accounts, current), { emitEvent: false });
  }

  findAllAsTreeView(contractId) {
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/tree-view/${contractId}`);
  }

  findAllAsSelect(
    noLimit?: boolean,
    sortField: string = 'label',
    sortOrder: string = 'asc',
    search?: string,
    filters?: any,
  ) {
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/findAllAsSelect`, {
      params: new HttpParams()
        .set('noLimit', noLimit)
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  async findAllAffectedToUser() {
    const user = await this.authService.currentUserValue();
    const establishmentIds = user.affectations.map(x => x.establishment._id);
    return lastValueFrom(
      this.findAllAsSelect(true, '_id', 'desc', '', { includeIds: establishmentIds }).pipe(
        map(res => (res?.data.length === 1 ? res.data[0] : null)),
      ),
    );
  }

  downloadExcelAddressTemplate(): Observable<ObjectApiInterface<AddressModel>> {
    return this.http.get<ObjectApiInterface<AddressModel>>(
      `${this.baseUrlApi}/download-excel-establishment-address-template`,
    );
  }

  // New unified template with two sheets (Care + Support)
  downloadExcelEstablishmentsTemplate(): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http.get<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/download-excel-template`);
  }

  // New unified import endpoint handling both sheets
  importEstablishments(data: any): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http.post<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/import-establishments`, data, {
      headers: { 'Error-CRUD': 'IMPORT' },
    });
  }

  exportEstablishmentAddresses(): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http.get<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/export-addresses`);
  }

  checkSiretExists(siret: string): Observable<{ exists: boolean }> {
    if (!siret) {
      return of({ exists: false });
    }
    return this.http.get<{ exists: boolean }>(`${this.baseUrlApi}/check-siret/${siret}`);
  }

  createContact(
    establishmentId: string,
    contact: SupplierContactModel,
  ): Observable<ObjectApiInterface<SupplierContactModel>> {
    return this.http
      .post<ObjectApiInterface<SupplierContactModel>>(`${this.baseUrlApi}/${establishmentId}/createContact`, contact)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          if (entity.contacts) {
            entity.contacts.push(x.data);
          } else {
            entity.contacts = [x.data];
          }
          this._entity.next(entity);
          return x;
        }),
      );
  }

  updateContact(
    establishmentId: string,
    contact: SupplierContactModel,
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    // Ne pas mettre à jour _entity pour éviter un refetch automatique
    // Le composant appelant gère le refresh manuellement via initData()
    return this.http.put<ObjectApiInterface<EstablishmentModel>>(
      `${this.baseUrlApi}/${establishmentId}/updateContact/${contact._id}`,
      contact,
    );
  }

  deleteContact(establishmentId: string, contactId: string): Observable<ObjectApiInterface<SupplierContactModel>> {
    return this.http
      .delete<
        ObjectApiInterface<SupplierContactModel>
      >(`${this.baseUrlApi}/${establishmentId}/deleteContact/${contactId}`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const foundIdx = entity.contacts?.findIndex(y => y._id === contactId);
          if (foundIdx !== undefined && foundIdx !== -1) {
            entity.contacts.splice(foundIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  createActivity(establishmentId: string, activity: ActivityModel): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .post<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/${establishmentId}/createActivity`, activity)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  addGenericPrestation(
    establishmentId: string,
    genericPrestation: EstablishmentGenericPrestationModel[],
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .post<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/addGenericPrestation`, genericPrestation)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          entity.genericPrestations = x.data.genericPrestations;
          this._entity.next(entity);
          return x;
        }),
      );
  }

  addGenericProduct(
    establishmentId: string,
    genericPrestation: EstablishmentGenericProductModel[],
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .post<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/addGenericProduct`, genericPrestation)
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          const updated = { ...current, genericProducts: x.data.genericProducts };
          this._entity.next(updated);
          return x;
        }),
      );
  }

  deleteGenericPrestation(id: string, genericPrestationId: string): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .delete<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${id}/deleteGenericPrestation/${genericPrestationId}`)
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          const updated = { ...current, genericPrestations: x.data.genericPrestations };
          this._entity.next(updated);
          return x;
        }),
      );
  }

  changeAutoDependenciesAndArchive(_id: string): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .put<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/${_id}/changeAutoDependenciesAndArchive`, {})
      .pipe(
        map((x: any) => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  deleteGenericProduct(id: string, genericProductId: string): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .delete<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${id}/deleteGenericProduct/${genericProductId}`)
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const foundIdx = entity.genericProducts.findIndex(y => y._id === genericProductId);
          if (foundIdx !== -1) {
            entity.genericProducts.splice(foundIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  deleteGenericPrestationsBatch(
    establishmentId: string,
    genericIds: string[],
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .delete<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/deleteGenericPrestationsBatch`, { body: { genericIds } })
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          const updated = {
            ...current,
            genericPrestations: x.data.genericPrestations,
          };
          this._entity.next(updated);
          return x;
        }),
      );
  }

  deleteGenericProductsBatch(
    establishmentId: string,
    genericIds: string[],
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .delete<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/deleteGenericProductsBatch`, { body: { genericIds } })
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const updatedProducts = entity.genericProducts.filter(product => !genericIds.includes(product._id));
          entity.genericProducts = updatedProducts;
          this._entity.next(entity);
          return x;
        }),
      );
  }

  archiveActivity(id: string, activityId: string): Observable<ObjectApiInterface<ActivityModel>> {
    return this.http
      .put<ObjectApiInterface<ActivityModel>>(`${this.baseUrlApi}/${id}/archiveActivity/${activityId}`, {})
      .pipe(
        map(x => {
          const entity = this._entity.getValue();
          const foundIdx = entity.activities.findIndex(y => y._id === activityId);
          if (foundIdx !== -1) {
            entity.activities.splice(foundIdx, 1);
            this._entity.next(entity);
          }
          return x;
        }),
      );
  }

  updateAnnualCharge(
    establishmentId: string,
    charge: AnnualChargeModel,
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .put<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/${establishmentId}/update-annual-charge`, charge)
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          current.annualCharges = x.data.annualCharges;
          this._entity.next(current);
          return x;
        }),
      );
  }

  updateActivity(id: string, data: any): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .put<ObjectApiInterface<EstablishmentModel>>(`${this.baseUrlApi}/${id}/updateActivity/${data._id}`, data)
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  // Analytics Methods
  createAnalyticsLine(establishmentId: string, data: any) {
    return this.http.post<any>(`${this.baseUrlApi}/${establishmentId}/analytics/line/create`, data).pipe(
      map(x => {
        // this._entity.lines.push(x);
        return x;
      }),
    );
  }

  archiveAnalyticsLine(establishmentId: string, data: any) {
    data._id = establishmentId;
    data.isArchive = true;
    return this.http.put<any>(`${this.baseUrlApi}/${establishmentId}/analytics/line/archive`, data).pipe(
      map(x => {
        return x;
      }),
    );
  }

  updateAnalyticsLine(establishmentId: string, data: any) {
    console.log(data);
    return this.http.put<any>(`${this.baseUrlApi}/${establishmentId}/analytics/line/update`, data).pipe(
      map(x => {
        return x;
      }),
    );
  }

  findAllAnalyticsLines(
    page?: number,
    perPage?: number,
    sortField: string = 'accountNumber',
    sortOrder: string = 'asc',
    search?: string,
    filters?: any,
  ) {
    if (!filters.isArchive) {
      filters.isArchive = false;
    }
    // TODO : if necessery add autocomplete when this function is call in bill to keep paginition and remove perPage 500
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/analytics/line/list`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? '500')
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  downloadExcelAnalyticLinesTemplate() {
    return this.http.get(`${this.baseUrlApi}/analytic/lines/download-analytic-excel-template`);
  }

  importAnalytic(establishmentId, entity) {
    return this.http.post(`${this.baseUrlApi}/${establishmentId}/analytic/line/import`, entity);
  }

  getThresholdForUserProfile(ids: Array<string>) {
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/get-seuil-for-userProfile`, {
      params: new HttpParams().set('ids', ids.join(',')),
    });
  }

  transformEstablishmentData(data: EstablishmentModel[]) {
    const transformedData = [];

    for (const establishment of data) {
      const transformedItem = {
        _id: establishment._id,
        label: establishment.label,
        genericLabel: null,
        genericId: null,
        establishmentId: null,
        establishmentLabel: null,
        buildingId: null,
        buildingLabel: null,
      };
      transformedData.push({ ...transformedItem });
    }

    transformedData.sort((a, b) => a.label.localeCompare(b.label));
    return transformedData;
  }

  updateMainBuilding(establishmentId: string, buildingId: any, favorite: boolean) {
    return this.http
      .put<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/updateMainBuilding/${buildingId}`, { favorite })
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  updateMainAddress(establishmentId: string, addressId: any, favorite: boolean) {
    return this.http
      .put<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/updateMainAddress/${addressId}`, { favorite })
      .pipe(
        map(x => {
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  addEstablishmentCustomer(
    establishmentId: string,
    establishmentCustomer: EstablishmentCustomerModel[],
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .put<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/add-customer`, establishmentCustomer)
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          const updated = { ...current, establishmentCustomers: x.data.establishmentCustomers };
          this._entity.next(updated);
          return x;
        }),
      );
  }

  deleteEstablishmentCustomer(
    id: string,
    establishmentCustomer: string,
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    return this.http
      .delete<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${id}/delete-customer/${establishmentCustomer}`)
      .pipe(
        map(x => {
          const current = this._entity.getValue();
          const updated = { ...current, establishmentCustomers: x.data.establishmentCustomers };
          this._entity.next(updated);
          return x;
        }),
      );
  }

  getImpacts(establishmentCustomer: EstablishmentCustomerModel) {
    const support = this._entity.getValue();
    const customerId = establishmentCustomer?.customer_id ?? establishmentCustomer?._id;
    return this.http.get<ObjectApiInterface<any>>(`${this.baseUrlApi}/${support._id}/customer-impacts/${customerId}`);
  }

  getNeedsImpacts(generic: any) {
    const support = this._entity.getValue();
    return this.http.get<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${support._id}/needs-impacts/${generic.generic_id}`,
    );
  }

  checkEstablishmentDependencies(establishmentId: string) {
    return this.http.post<ObjectApiInterface<any>>(`${this.baseUrlApi}/checkDependencies/${establishmentId}`, {});
  }

  findAllSupervision(
    ticketId: string,
    establishmentId: string,
    page?: number,
    perPage?: number,
    sortField: string = this.defaultSort,
    sortOrder: string = 'desc',
    search?: string,
    filters?: any,
  ): Observable<ObjectApiInterface<any[]>> {
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/list/${establishmentId}/${ticketId}`, {
      params: new HttpParams()
        .set('page', page?.toString() ?? '1')
        .set('perPage', perPage?.toString() ?? DEFAULT_PAGE_SIZE.toString())
        .set('sort', `${sortField}@${sortOrder}`)
        .set('search', search ?? '')
        .set('filters', JSON.stringify(filters) ?? ''),
    });
  }

  /**
   * Archive un contact interne pour l'établissement support spécifié
   */
  archiveInternalContact(establishmentId: string, userProfileId: string): Observable<ObjectApiInterface<any>> {
    return this.http.put<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${establishmentId}/archive-internal-contact/${userProfileId}`,
      {},
    );
  }

  /**
   * Désarchive un contact interne pour l'établissement support spécifié
   */
  unarchiveInternalContact(establishmentId: string, userProfileId: string): Observable<ObjectApiInterface<any>> {
    return this.http.put<ObjectApiInterface<any>>(
      `${this.baseUrlApi}/${establishmentId}/unarchive-internal-contact/${userProfileId}`,
      {},
    );
  }

  /**
   * Récupère la liste des contacts internes archivés pour l'établissement support
   */
  getArchivedInternalContacts(establishmentId: string): Observable<ObjectApiInterface<any[]>> {
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/${establishmentId}/internal-contacts-archived`);
  }

  /**
   * Récupère la liste des contacts internes actifs (non archivés) pour l'établissement support
   */
  getActiveInternalContacts(
    establishmentId: string,
    search?: string,
    requiredFields?: string[],
  ): Observable<ObjectApiInterface<any[]>> {
    let params = new HttpParams();
    if (search) {
      params = params.set('search', search);
    }
    if (requiredFields) {
      params = params.set('requiredFields', JSON.stringify(requiredFields));
    }
    return this.http.get<ObjectApiInterface<any[]>>(`${this.baseUrlApi}/${establishmentId}/internal-contacts-active`, {
      params,
    });
  }

  analyzeEstablishmentContractsCompatibility(
    establishmentId: string,
    newMainEstablishmentId?: string,
  ): Observable<ObjectApiInterface<ContractCompatibilityAnalysis>> {
    if (!establishmentId) {
      throw new Error("establishmentId est requis pour l'analyse des contrats");
    }

    const body = {
      establishmentId,
      newMainEstablishmentId,
    };
    return this.http.post<ObjectApiInterface<ContractCompatibilityAnalysis>>(
      `${this.baseUrlApi}/analyzeEstablishmentContractsCompatibility`,
      body,
    );
  }

  /**
   * Met à jour l'établissement principal d'un établissement support
   * @param establishmentId ID de l'établissement support à modifier
   * @param newMainEstablishmentId ID du nouvel établissement principal (null pour supprimer)
   * @param additionalData Données supplémentaires (SIREN, SIRET, TVA pour mode indépendant)
   */
  updateMainEstablishment(
    establishmentId: string,
    newMainEstablishmentId: string | null,
    additionalData?: any,
  ): Observable<ObjectApiInterface<EstablishmentModel>> {
    console.log('newMainEstablishmentId', newMainEstablishmentId);
    const body = {
      additionalData,
      newMainEstablishmentId,
    };

    return this.http
      .put<
        ObjectApiInterface<EstablishmentModel>
      >(`${this.baseUrlApi}/${establishmentId}/update-main-establishment`, body)
      .pipe(
        map(x => {
          // Mettre à jour l'entité locale avec les nouvelles données
          this._entity.next(x.data);
          return x;
        }),
      );
  }

  /**
   * Récupère les activités disponibles pour la distribution sur un ticket,
   * filtrées selon le périmètre du déclarant si declarantId est fourni.
   */
  getActivitiesForDistribution(
    establishmentId: string,
    declarantId?: string,
  ): Observable<ObjectApiInterface<ActivityForDistributionResponse[]>> {
    let params = new HttpParams();
    if (declarantId) {
      params = params.set('declarantId', declarantId);
    }
    return this.http.get<ObjectApiInterface<ActivityForDistributionResponse[]>>(
      `${this.baseUrlApi}/${establishmentId}/activities-for-distribution`,
      { params },
    );
  }
}
